// src/admin/pages/customers/CustomerTable.tsx
import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Block as BlockIcon, CheckCircle as ActivateIcon } from '@mui/icons-material';
import { Customer } from './customerService';

interface CustomerTableProps {
  customers: Customer[];
  onToggleStatus: (customer: Customer) => void;
  toggleLoading: string;
  isDark?: boolean;
  canEdit?: boolean;
}

const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string };

  const lightPalette: Record<string, PaletteEntry> = {
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.success;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-label': { color: p.text },
  };
};

const CustomerTable: React.FC<CustomerTableProps> = ({
  customers,
  onToggleStatus,
  toggleLoading,
  isDark = false,
  canEdit = false,
}) => {
  return (
    <TableContainer
      component={Paper}
      sx={{
        height: '100%',
        '& .MuiTable-root': { minWidth: 800 },
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Customer</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell>Registered On</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {customers.map((c) => (
            <TableRow
              key={c.uid}
              hover
              sx={{
                '&:hover': { backgroundColor: 'action.hover' },
                '&:last-child td, &:last-child th': { borderBottom: 0 },
              }}
            >
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>
                    {c.fullName?.[0]?.toUpperCase() || '?'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {c.fullName || 'Unnamed customer'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.email}
                    </Typography>
                  </Box>
                </Stack>
              </TableCell>

              <TableCell>
                <Typography variant="body2">{c.phone || '—'}</Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {c.createdAt ? c.createdAt.toLocaleDateString() : '—'}
                </Typography>
              </TableCell>

              <TableCell>
                <Chip
                  label={c.isActive ? 'Active' : 'Deactivated'}
                  size="small"
                  variant="outlined"
                  sx={getSoftChipSx(c.isActive ? 'success' : 'error', isDark)}
                />
              </TableCell>

              <TableCell align="right">
                {canEdit && (
                  <Tooltip title={c.isActive ? 'Deactivate customer' : 'Activate customer'}>
                    <IconButton
                      onClick={() => onToggleStatus(c)}
                      disabled={toggleLoading === c.uid}
                      size="small"
                      color={c.isActive ? 'error' : 'success'}
                    >
                      {toggleLoading === c.uid ? (
                        <CircularProgress size={16} />
                      ) : c.isActive ? (
                        <BlockIcon fontSize="small" />
                      ) : (
                        <ActivateIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CustomerTable;
